
import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { APIError, useAxios } from '../../core/_axios';
import { Service, ServiceFamilyMember } from '../../core/_models';
import { addFamilyMemberToServiceByUniqueIdentifier, endServiceByUniqueIdentifier, getServiceFamilyMembersByUniqueIdentifier } from '../../core/_requests';
import usePageTitle from '../../hooks/usePageTitle';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function AddFamilyMemberToServiceForm({
    service,
}: {
    service: Service,
}) {
    const axios = useAxios();

    const navigate = useNavigate();

    usePageTitle('Add Family Member to Service');

    const [addingFamilyMember, setAddingFamilyMember] = useState(false);
    const [errorAddingFamilyMember, setErrorAddingFamilyMember] = useState<string | null>(null);

    const onStartServiceFormSubmitted = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (addingFamilyMember) {
            return;
        }
        setErrorAddingFamilyMember(null);
        setAddingFamilyMember(true);
        try {
            const data = new FormData(event.currentTarget);
            await addFamilyMemberToServiceByUniqueIdentifier(axios, service.unique_identifier, {
                ...Object.fromEntries(data.entries())
            });
            // Go immediately to the family member listing page
            navigate(`/services/${service.unique_identifier}/send-service-to-family`);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorAddingFamilyMember(errorMessage)
            setAddingFamilyMember(false)
        }
    }, [axios, addingFamilyMember, navigate, service.unique_identifier]);

    return <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
        <form className="col-span-2" onSubmit={onStartServiceFormSubmitted}>
            <div className="flex flex-col gap-5 lg:gap-7.5">
                <div className="card">
                    <div className="card-header">
                        <h3 className="card-title">
                            {`Send ${service.public_data.deceased_person.full_name}'s Service to Family`}
                        </h3>
                    </div>
                    <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                        <div className="flex flex-col gap-4">
                            {service.public_data.deceased_person.avatar_url && <div>
                                <div>
                                    <img src={
                                        service.public_data.deceased_person.avatar_url
                                    } className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-24" alt="avatar" />
                                </div>
                            </div>}
                            <div className="text-lg font-medium">
                                {service.public_data.deceased_person.full_name}
                            </div>
                        </div>
                        <div className="flex flex-col gap-4">
                            <div>
                                <label
                                    htmlFor="family_member_full_name"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {`Family Member's Full Name (required)`}
                                </label>
                                <input
                                    id="family_member_full_name"
                                    name="family_member_full_name"
                                    type="text"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                    placeholder="John Doe"
                                    required
                                    autoComplete='off'
                                    autoFocus={!service}
                                />
                            </div>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2">
                            <div>
                                <label htmlFor="family_member_email_address" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {`Family Member's Email Address (required)`}
                                </label>
                                <input
                                    type="email"
                                    id="family_member_email_address"
                                    name="family_member_email_address"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                    placeholder="person@gmail.com"
                                    required={true}
                                />
                            </div>
                            <div>
                                <label htmlFor="family_member_phone_number" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {`Family Member's Phone Number (optional)`}
                                </label>
                                <input
                                    type="text"
                                    id="family_member_phone_number"
                                    name="family_member_phone_number"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                    placeholder="555-555-5555"
                                    required={false}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-4">
                            <div>
                                <label
                                    htmlFor="family_member_relationship"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {`Family Member's Relationship to ${service.public_data.deceased_person.full_name} (optional)`}
                                </label>
                                <input
                                    id="family_member_relationship"
                                    name="family_member_relationship"
                                    type="text"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                    placeholder="Brother"
                                    required={false}
                                    autoComplete='off'
                                />
                            </div>
                        </div>
                    </div>
                    <div className="card-footer py-3.5 flex flex-col lg:flex-row items-center justify-between">
                        <div>
                            {errorAddingFamilyMember && <div className="text-red-500 text-sm font-medium">{errorAddingFamilyMember}</div>}
                        </div>
                        <div>
                            <Link
                                to={`/services/${service.unique_identifier}/send-service-to-family`}
                                className="btn btn-sm btn-light me-2"
                            >
                                Cancel
                            </Link>
                            <button
                                type="submit"
                                className="btn btn-sm btn-primary"
                                disabled={addingFamilyMember}
                            >
                                {!addingFamilyMember ? `Add Family Member` : `Adding...`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </div>
}